<template>
  <div class="analytics-panel">
    <div class="analytics-section">
      <div class="analytics-section-title">Audit</div>
      <p class="audit-note">Sign-ins, failed attempts, lockouts, sign-outs, sessions that ended, and reads, exports, and edits. The person can be someone signed in to the app, the account that opened the designer, or an API key. Field values and passwords are not stored.</p>
      <div v-if="error" class="analytics-empty">{{ error }}</div>
      <div v-else-if="entries.length === 0" class="analytics-empty">No sign-in activity recorded yet.</div>
      <table v-else class="analytics-table">
        <thead>
          <tr>
            <th>when</th>
            <th>person</th>
            <th>event</th>
            <th>where</th>
            <th>detail</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in entries" :key="entry.id">
            <td>{{ formatWhen(entry.at) }}</td>
            <td>{{ entry.username || '–' }}</td>
            <td>{{ eventLabel(entry.action) }}</td>
            <td>{{ entry.target || '–' }}</td>
            <td>{{ detailLabel(entry) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>

import axios from 'axios'

const EVENT_LABELS = {
  signed_in: 'signed in',
  sign_in_failed: 'sign-in failed',
  locked: 'locked out',
  signed_out: 'signed out',
  session_ended: 'session ended',
  read: 'read',
  export: 'export',
  edit: 'edit'
}

const REASON_LABELS = {
  unknown: 'unknown person',
  password: 'wrong password',
  not_hashed: 'stored password is not a hash',
  idle: 'inactivity',
  expired: 'session length',
  records: 'records',
  csv: 'csv',
  calendar: 'calendar',
  save: 'save',
  delete: 'delete',
  change: 'change'
}

export default {
  name: 'AnalyticsAudit',
  props: {
    application: { type: String, required: true },
    from: { type: String, default: null },
    to: { type: String, default: null },
    refreshKey: { type: Number, default: 0 }
  },
  data () {
    return {
      entries: [],
      error: ''
    }
  },
  watch: {
    from () { this.load() },
    to () { this.load() },
    refreshKey () { this.load() },
    application () { this.load() }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.error = ''
      try {
        const params = {}
        if (this.from) params.from = this.from
        if (this.to) params.to = this.to
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/audit`, {
          headers: { 'application-unique-title': this.application },
          params
        })
        this.entries = (res.data && res.data.entries) || []
      } catch (err) {
        this.entries = []
        this.error = 'Could not load the audit log.'
      }
    },
    eventLabel (action) {
      return EVENT_LABELS[action] || action || '–'
    },
    detailLabel (entry) {
      if (!entry.reason) return '–'
      return REASON_LABELS[entry.reason] || entry.reason
    },
    formatWhen (value) {
      if (!value) return '–'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '–'
      return date.toLocaleString()
    }
  }
}

</script>

<style scoped>
.analytics-panel {
  padding: 16px 18px 28px;
}

.analytics-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 4px;
}

.audit-note {
  font-size: 0.75rem;
  color: #707070;
  margin: 0 0 12px;
}

.analytics-empty {
  color: #707070;
  font-size: 0.8rem;
  padding: 12px 0;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.analytics-table th {
  text-align: left;
  color: #707070;
  font-weight: 500;
  padding: 6px 10px;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.analytics-table td {
  padding: 7px 10px;
  color: #a0a09e;
  border-bottom: 1px solid #1e1e1b;
}

.analytics-table tr:hover td {
  background: #1a1a17;
}
</style>
